import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useIncidentActions } from 'components/IAP/hooks/incidentActionHooks';
import { useGetRosterForIncident } from 'hooks/incident/useRoster';
import type { CardOption } from 'components/IAP/planningView/cardOptions';
import { PLANNING_PHASES } from 'components/IAP/planningView/USCGPage';
import { FormPaneRow } from 'components/IAP/planningView/FormPaneRow';
import {
  FormsDropdown,
  useWidgetRenderContext,
} from 'components/IAP/planningView/widgetRegistry';
import { CorporateViewTabGroup } from 'components/MapPage/CorporateViewTabGroup';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { CorporateViewTabs } from 'components/MapPage/hubNavTabs';
import { registerSitlCopTourApi } from 'components/MapPage/sitlCopTour/sitlCopTourBridge';
import { SITL_COP_TOUR_PARAM } from 'components/MapPage/sitlCopTour/sitlCopTourSteps';
import { registerFocWsTourApi } from 'components/MapPage/focWsTour/focWsTourBridge';
import { FOC_WS_TOUR_PARAM } from 'components/MapPage/focWsTour/focWsTourSteps';
import { registerFocCmdTourApi } from 'components/MapPage/focCmdTour/focCmdTourBridge';
import { findFocCmdTourEntryByParam } from 'components/MapPage/focCmdTour/focCmdTourRegistry';
import {
  FOC_DMOB_SEAT_SLUG,
  FOC_DMOB_TOUR_IAP_PARAM,
} from 'components/MapPage/focDmobTour/focDmobTourSteps';
import {
  findFocPlnTourEntryByParam,
  focPlnWorkspaceTourRoot,
} from 'components/MapPage/focPlnTour/focPlnTourRegistry';
import {
  FOC_OPS_SEAT_SLUG,
  FOC_OPS_TOUR_PARAM,
} from 'components/MapPage/focOpsTour/focOpsTourSteps';
import {
  FOC_LOG_SEAT_SLUG,
  FOC_LOG_TOUR_PARAM,
} from 'components/MapPage/focLogTour/focLogTourSteps';
import { registerFocFsltpTourApi } from 'components/MapPage/focFsltpTour/focFsltpTourBridge';
import {
  focFsltpHubDailyHref,
  focFsltpLayersHref,
} from 'components/MapPage/focFsltpTour/focFsltpTourSteps';
import { UscgMapLayout } from 'components/MapPage/UscgMapLayout';
import { SITL_SEAT_SLUG } from 'components/MapPage/utils/mapExclusiveSurfaces';
import {
  usePositionWorkspacesFlag,
  useUscgFocRequirementsFlag,
} from 'utils/featureFlags';

import { tasksForPositions, type Deliverable, type TaskRow } from './deliverables';
import { formOptionsForSeat } from './seatForms';
import { rosterNamesForSeat, seatBySlug } from './seatCatalog';

const STATUS_VARIANT = {
  'Not started': 'secondary',
  'In progress': 'blue',
  'Ready for review': 'amber',
  'Ready for the meeting': 'amber',
  'Needs revision': 'amber',
  Approved: 'green',
  Superseded: 'secondary',
  Complete: 'green',
} as const;

function toTaskRow(action: {
  action_id: string;
  status: string | null;
  target_date: Date | null;
  assignee_kind?: string | null;
  assignee_id?: string | null;
  blocked_by?: { action_id: string }[];
  description?: string | null;
  name?: string | null;
}): TaskRow {
  return {
    id: action.action_id,
    title: action.name || action.description || 'Task',
    status: action.status,
    due: action.target_date ? String(action.target_date) : null,
    assigneeKind: action.assignee_kind,
    assigneeId: action.assignee_id,
    blockedBy: action.blocked_by,
  };
}

function positionIdsFor(
  roster: { incidentPositions: { positionId: string; positionName: string }[] }[],
  positionNames: readonly string[]
): Set<string> {
  const names = new Set(positionNames);
  const ids = new Set<string>();
  for (const member of roster) {
    for (const position of member.incidentPositions) {
      if (names.has(position.positionName)) ids.add(position.positionId);
    }
  }
  return ids;
}

function isSitlCopSurface(openTab: string | null): boolean {
  return (
    openTab === CorporateViewTabs.SitStat ||
    openTab === CorporateViewTabs.Layers ||
    openTab === CorporateViewTabs.Presentation
  );
}

export function PositionWorkspace() {
  const workspacesOn = usePositionWorkspacesFlag();
  const focRequirements = useUscgFocRequirementsFlag();
  const { incidentId, seatSlug } = useParams<{
    incidentId: string;
    seatSlug: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setIsMapVisible } = useUscgMapContext();
  const seat = seatSlug ? seatBySlug(seatSlug) : undefined;
  const isSitlSeat = seatSlug === SITL_SEAT_SLUG;
  const { data: actions = [] } = useIncidentActions();
  const { data: roster = [] } = useGetRosterForIncident();
  const widgetContext = useWidgetRenderContext(PLANNING_PHASES, 0);
  const formOptions = seat ? formOptionsForSeat(seat.relatedForms) : [];
  const [chosen, setChosen] = useState<string | null>(null);
  const selectedForm =
    formOptions.find((option) => option.value === chosen)?.value ??
    formOptions[0]?.value ??
    '';
  const openTab = searchParams.get('openTab');
  const tourStartedRef = useRef(false);
  const focWsTourStartedRef = useRef(false);
  const focCmdTourStartedRef = useRef(false);
  const focDmobTourStartedRef = useRef(false);
  const focPlnTourStartedRef = useRef(false);
  const focOpsTourStartedRef = useRef(false);
  const focLogTourStartedRef = useRef(false);

  const openSitlSurface = (tab: string | null) => {
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        if (tab) {
          next.set('openTab', tab);
          setIsMapVisible(true);
        } else {
          next.delete('openTab');
        }
        return next;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (!isSitlSeat) return;
    return registerSitlCopTourApi({
      showForms: () => openSitlSurface(null),
      openSitStat: () => openSitlSurface(CorporateViewTabs.SitStat),
      openLayers: () => openSitlSurface(CorporateViewTabs.Layers),
      openPresentation: () =>
        openSitlSurface(CorporateViewTabs.Presentation),
      openAddInternalLayer: () => {
        openSitlSurface(CorporateViewTabs.SitStat);
      },
      openReviewQueue: () => {
        openSitlSurface(CorporateViewTabs.Layers);
      },
      showSymbology: () => {
        openSitlSurface(CorporateViewTabs.Layers);
      },
    });
    // openSitlSurface closes over setSearchParams / setIsMapVisible.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- register once per seat
  }, [isSitlSeat, setSearchParams, setIsMapVisible]);

  useEffect(() => {
    if (!isSitlSeat) return;
    return registerFocFsltpTourApi({
      getLocation: () => ({
        pathname: window.location.pathname,
        search: window.location.search,
      }),
      openSitlPresentation: () =>
        openSitlSurface(CorporateViewTabs.Presentation),
      showLayers: () => navigate(focFsltpLayersHref()),
      openHubDaily: () => navigate(focFsltpHubDailyHref()),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- register once per seat
  }, [isSitlSeat, navigate, setSearchParams, setIsMapVisible, openTab]);

  const wantsSitlCopTour = searchParams.get(SITL_COP_TOUR_PARAM) === '1';
  const wantsFocWsTour = searchParams.get(FOC_WS_TOUR_PARAM) === '1';
  const focCmdTourEntry = findFocCmdTourEntryByParam(searchParams);
  const wantsFocCmdTour =
    Boolean(focCmdTourEntry) &&
    focCmdTourEntry?.seatSlug === seatSlug;
  const wantsFocDmobTour =
    seatSlug === FOC_DMOB_SEAT_SLUG &&
    searchParams.get(FOC_DMOB_TOUR_IAP_PARAM) === '1';
  const focPlnTourEntry = findFocPlnTourEntryByParam(searchParams);
  const wantsFocPlnTour =
    Boolean(focPlnTourEntry) && focPlnTourEntry?.seatSlug === seatSlug;
  const wantsFocOpsTour =
    seatSlug === FOC_OPS_SEAT_SLUG &&
    searchParams.get(FOC_OPS_TOUR_PARAM) === '1';
  const wantsFocLogTour =
    seatSlug === FOC_LOG_SEAT_SLUG &&
    searchParams.get(FOC_LOG_TOUR_PARAM) === '1';
  const isDemobSeat = seatSlug === FOC_DMOB_SEAT_SLUG;
  const workspaceTourRoot =
    seatSlug === FOC_OPS_SEAT_SLUG
      ? 'ops-workspace'
      : seatSlug === FOC_LOG_SEAT_SLUG
        ? 'log-workspace'
        : focPlnWorkspaceTourRoot(seatSlug);

  useEffect(() => {
    const scrollToTour = (tour: string) => {
      window.setTimeout(() => {
        const el = document.querySelector(`[data-tour="${tour}"]`);
        if (el instanceof HTMLElement) {
          el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 50);
    };
    const unregisterWs = registerFocWsTourApi({ scrollToTour });
    const unregisterCmd = registerFocCmdTourApi({ scrollToTour });
    return () => {
      unregisterWs();
      unregisterCmd();
    };
  }, []);

  useEffect(() => {
    if (!wantsSitlCopTour || !isSitlSeat) {
      tourStartedRef.current = false;
      return;
    }
    if (!focRequirements || tourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      tourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(SITL_COP_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('components/MapPage/sitlCopTour/sitlCopTour')
        .then(({ startSitlCopTour }) => {
          startSitlCopTour();
        })
        .catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [
    wantsSitlCopTour,
    isSitlSeat,
    focRequirements,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!wantsFocWsTour) {
      focWsTourStartedRef.current = false;
      return;
    }
    if (!focRequirements || focWsTourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      if (focWsTourStartedRef.current) return;
      focWsTourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_WS_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('components/MapPage/focWsTour/focWsTour')
        .then(async ({ startFocWsTour, waitForSelector }) => {
          await waitForSelector('[data-tour="position-workspace-root"]');
          startFocWsTour();
        })
        .catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [wantsFocWsTour, focRequirements, setSearchParams]);

  useEffect(() => {
    if (!wantsFocCmdTour || !focCmdTourEntry) {
      focCmdTourStartedRef.current = false;
      return;
    }
    if (!focRequirements || focCmdTourStartedRef.current) return;

    const entry = focCmdTourEntry;
    const timer = window.setTimeout(() => {
      if (focCmdTourStartedRef.current) return;
      focCmdTourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(entry.param);
          return params;
        },
        { replace: true }
      );
      void import('components/MapPage/focCmdTour/focCmdTour')
        .then(async ({ startFocCmdTour, waitForSelector }) => {
          await waitForSelector('[data-tour="position-workspace-root"]');
          startFocCmdTour(entry.steps);
        })
        .catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [wantsFocCmdTour, focCmdTourEntry, focRequirements, setSearchParams]);

  useEffect(() => {
    if (!wantsFocDmobTour) {
      focDmobTourStartedRef.current = false;
      return;
    }
    if (!focRequirements || focDmobTourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      if (focDmobTourStartedRef.current) return;
      focDmobTourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_DMOB_TOUR_IAP_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('components/MapPage/focDmobTour/focDmobTour')
        .then(async ({ startFocDmobTour, waitForSelector }) => {
          await waitForSelector('[data-tour="demob-workspace"]');
          startFocDmobTour();
        })
        .catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [wantsFocDmobTour, focRequirements, setSearchParams]);

  useEffect(() => {
    if (!wantsFocPlnTour || !focPlnTourEntry) {
      focPlnTourStartedRef.current = false;
      return;
    }
    if (!focRequirements || focPlnTourStartedRef.current) return;

    const entry = focPlnTourEntry;
    const timer = window.setTimeout(() => {
      if (focPlnTourStartedRef.current) return;
      focPlnTourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(entry.param);
          return params;
        },
        { replace: true }
      );
      void import('components/MapPage/focPlnTour/focPlnTour')
        .then(async ({ startFocPlnTourForEntry, waitForSelector }) => {
          await waitForSelector(`[data-tour="${entry.rootTour}"]`);
          startFocPlnTourForEntry(entry);
        })
        .catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [wantsFocPlnTour, focPlnTourEntry, focRequirements, setSearchParams]);

  useEffect(() => {
    if (!wantsFocOpsTour) {
      focOpsTourStartedRef.current = false;
      return;
    }
    if (!focRequirements || focOpsTourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      if (focOpsTourStartedRef.current) return;
      focOpsTourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_OPS_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('components/MapPage/focOpsTour/focOpsTour')
        .then(async ({ startFocOpsTour, waitForSelector }) => {
          await waitForSelector('[data-tour="ops-workspace"]');
          startFocOpsTour();
        })
        .catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [wantsFocOpsTour, focRequirements, setSearchParams]);

  useEffect(() => {
    if (!wantsFocLogTour) {
      focLogTourStartedRef.current = false;
      return;
    }
    if (!focRequirements || focLogTourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      if (focLogTourStartedRef.current) return;
      focLogTourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_LOG_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('components/MapPage/focLogTour/focLogTour')
        .then(async ({ startFocLogTour, waitForSelector }) => {
          await waitForSelector('[data-tour="log-workspace"]');
          startFocLogTour();
        })
        .catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [wantsFocLogTour, focRequirements, setSearchParams]);

  if (!workspacesOn) {
    return (
      <Navigate to={`/iap/${incidentId ?? ''}/dashboard`} replace />
    );
  }

  if (!incidentId || !seat) {
    return (
      <div className="dark tw:bg-background tw:p-4 tw:text-foreground">
        <p>This position workspace is not on the incident.</p>
        <Button
          variant="link"
          size="sm"
          className="tw:h-auto tw:px-0"
          render={<Link to={`/iap/${incidentId ?? ''}/dashboard`} />}
        >
          Planning-P
        </Button>
      </div>
    );
  }

  if (isSitlSeat && isSitlCopSurface(openTab)) {
    return (
      <div
        className="d-flex flex-column h-100 w-100"
        data-tour="sitl-cop-workspace"
        style={{ minHeight: 0, overflow: 'hidden' }}
      >
        <SitlCopChrome
          title={seat.label}
          backTo={
            searchParams.get('returnPhase')
              ? `/iap/${incidentId}/dashboard?phase=${searchParams.get('returnPhase')}`
              : `/iap/${incidentId}/dashboard`
          }
          activeSurface={openTab}
          onOpenForms={() => openSitlSurface(null)}
          onOpenSitStat={() => openSitlSurface(CorporateViewTabs.SitStat)}
          onOpenLayers={() => openSitlSurface(CorporateViewTabs.Layers)}
          onOpenPresentation={() =>
            openSitlSurface(CorporateViewTabs.Presentation)
          }
        />
        <div className="tw:min-h-0 tw:flex-1" style={{ overflow: 'hidden' }}>
          <UscgMapLayout>
            <CorporateViewTabGroup
              ics
              currentMode=""
              updateMode={() => {}}
              currentPhase={0}
            />
          </UscgMapLayout>
        </div>
      </div>
    );
  }

  const positionIds = positionIdsFor(roster, rosterNamesForSeat(seat));
  const taskRows = tasksForPositions(
    actions.map(toTaskRow),
    positionIds,
    seat.section,
    seat.label
  );
  const returnPhase = searchParams.get('returnPhase');
  const back = returnPhase
    ? `/iap/${incidentId}/dashboard?phase=${returnPhase}`
    : `/iap/${incidentId}/dashboard`;

  return (
    <SeatWorkspaceView
      title={seat.label}
      backTo={back}
      incidentId={incidentId}
      formOptions={formOptions}
      selectedForm={selectedForm}
      onSelectForm={setChosen}
      demobWorkspace={isDemobSeat}
      workspaceTourRoot={workspaceTourRoot}
      sitlCopChrome={
        isSitlSeat
          ? {
              activeSurface: null,
              onOpenForms: () => openSitlSurface(null),
              onOpenSitStat: () => openSitlSurface(CorporateViewTabs.SitStat),
              onOpenLayers: () => openSitlSurface(CorporateViewTabs.Layers),
              onOpenPresentation: () =>
                openSitlSurface(CorporateViewTabs.Presentation),
            }
          : undefined
      }
      form={
        <FormPaneRow
          options={formOptions}
          initialWidget={selectedForm}
          ctx={widgetContext}
          incidentId={incidentId}
          onPrimaryChange={setChosen}
        />
      }
      tasks={taskRows}
    />
  );
}

function SitlCopChrome({
  title,
  backTo,
  activeSurface,
  onOpenForms,
  onOpenSitStat,
  onOpenLayers,
  onOpenPresentation,
}: {
  title: string;
  backTo: string;
  activeSurface: string | null;
  onOpenForms: () => void;
  onOpenSitStat: () => void;
  onOpenLayers: () => void;
  onOpenPresentation: () => void;
}) {
  return (
    <header
      className="dark tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2 tw:border-b tw:border-border tw:bg-background tw:px-4 tw:py-2 tw:text-foreground"
      data-tour="sitl-cop-chrome"
    >
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <h1 className="tw:m-0 tw:text-lg tw:font-medium">{title}</h1>
        <div
          className="tw:flex tw:flex-wrap tw:items-center tw:gap-1"
          data-tour="sitl-cop-toolkit"
        >
          <Button
            type="button"
            size="sm"
            variant={activeSurface == null ? 'default' : 'outline'}
            data-tour="sitl-cop-open-forms"
            onClick={onOpenForms}
          >
            Forms
          </Button>
          <Button
            type="button"
            size="sm"
            variant={
              activeSurface === CorporateViewTabs.SitStat ? 'default' : 'outline'
            }
            data-tour="sitl-cop-open-sitstat"
            onClick={onOpenSitStat}
          >
            SitStat
          </Button>
          <Button
            type="button"
            size="sm"
            variant={
              activeSurface === CorporateViewTabs.Layers ? 'default' : 'outline'
            }
            data-tour="sitl-cop-open-layers"
            onClick={onOpenLayers}
          >
            Map Layers
          </Button>
          <Button
            type="button"
            size="sm"
            variant={
              activeSurface === CorporateViewTabs.Presentation
                ? 'default'
                : 'outline'
            }
            data-tour="sitl-cop-open-presentation"
            onClick={onOpenPresentation}
          >
            Presentation
          </Button>
        </div>
      </div>
      <Button
        variant="link"
        size="sm"
        className="tw:h-auto tw:px-0"
        render={<Link to={backTo} />}
      >
        Return to Planning-P
      </Button>
    </header>
  );
}

export function SeatWorkspaceView({
  title,
  backTo,
  incidentId,
  formOptions,
  selectedForm,
  onSelectForm,
  form,
  tasks,
  sitlCopChrome,
  demobWorkspace,
  workspaceTourRoot,
}: {
  title: string;
  backTo: string;
  incidentId: string;
  formOptions: CardOption[];
  selectedForm: string;
  onSelectForm: (value: string) => void;
  form?: ReactNode;
  tasks: Deliverable[];
  demobWorkspace?: boolean;
  workspaceTourRoot?: string | null;
  sitlCopChrome?: {
    activeSurface: string | null;
    onOpenForms: () => void;
    onOpenSitStat: () => void;
    onOpenLayers: () => void;
    onOpenPresentation: () => void;
  };
}) {
  const rootTour = sitlCopChrome
    ? 'sitl-cop-workspace'
    : workspaceTourRoot ||
      (demobWorkspace ? 'demob-workspace' : 'position-workspace-root');

  return (
    <div
      className="dark tw:flex tw:flex-col tw:gap-6 tw:bg-background tw:p-4 tw:text-foreground"
      data-tour={rootTour}
    >
      {sitlCopChrome ? (
        <SitlCopChrome
          title={title}
          backTo={backTo}
          activeSurface={sitlCopChrome.activeSurface}
          onOpenForms={sitlCopChrome.onOpenForms}
          onOpenSitStat={sitlCopChrome.onOpenSitStat}
          onOpenLayers={sitlCopChrome.onOpenLayers}
          onOpenPresentation={sitlCopChrome.onOpenPresentation}
        />
      ) : (
        <header className="tw:flex tw:items-center tw:justify-between tw:gap-2">
          <h1 className="tw:m-0 tw:text-lg tw:font-medium">{title}</h1>
          <span data-tour="position-workspace-return">
            <Button
              variant="link"
              size="sm"
              className="tw:h-auto tw:px-0"
              render={<Link to={backTo} />}
            >
              Return to Planning-P
            </Button>
          </span>
        </header>
      )}
      <section
        className="tw:flex tw:flex-col tw:gap-3"
        data-tour="position-workspace-forms"
      >
        <h2 className="tw:m-0 tw:text-base tw:font-medium">Forms</h2>
        {form ??
          (formOptions.length === 0 ? (
            <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
              No ICS forms for this seat.
            </p>
          ) : (
            <FormsDropdown
              id="seat-forms-dropdown"
              options={formOptions}
              value={selectedForm}
              onChange={onSelectForm}
            />
          ))}
      </section>
      <section
        className="tw:flex tw:flex-col tw:gap-3"
        data-tour="position-workspace-tasks"
      >
        <h2 className="tw:m-0 tw:text-base tw:font-medium">Tasks</h2>
        {tasks.length === 0 ? (
          <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
            No tasks are assigned to this position.
          </p>
        ) : (
          <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-2 tw:p-0">
            {tasks.map((task) => {
              const variant =
                STATUS_VARIANT[task.status as keyof typeof STATUS_VARIANT] ??
                'secondary';
              return (
                <li
                  key={task.id}
                  className="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:text-sm"
                >
                  <Badge variant={variant}>{task.status}</Badge>
                  {task.href ? (
                    <Button
                      variant="link"
                      size="sm"
                      className="tw:h-auto tw:px-0"
                      render={
                        <Link to={`/iap/${incidentId}/${task.href}`} />
                      }
                    >
                      {task.title}
                    </Button>
                  ) : (
                    <span>{task.title}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
